import { Icon } from '@fv/client-components'
import { formatNumber, getTrackingNumLabel } from '@fv/client-core'
import { fvDate } from '@fv/dates'
import { displayEquipment } from '@fv/models/equipment'

import { List, ListItem } from '@/components/List'
import { Loading } from '@/components/Loading'
import { ShipmentItemTypeSummary } from '@/components/ShipmentItemTypeSummary'
import { useLoad } from '@/hooks/shipments'
import { getCarrierDetails } from '@/utils/shipmentFuncs'

import { quoteFuncs } from '../quote/quoteFuncs'
import { formatTrackingDateTime } from '../track/utils'

type Props = {
  loadId: string
}

export const DayShipmentSummary = ({ loadId }: Props) => {
  const { data: shipment, isLoading } = useLoad(loadId)

  if (isLoading || !shipment) {
    return (
      <div className="bg-white flex-1 p-4 border border-fv-gray">
        <Loading>Loading shipment</Loading>
      </div>
    )
  }

  const origin = shipment.locations?.find(loc => loc.type === 'origin')
  const dest = shipment.locations?.find(loc => loc.type === 'destination')
  const isDelivered = shipment.tracking?.currentStatus === 'delivered'
  const estimatedDelivery = quoteFuncs.getEstimatedDeliveryDate(
    fvDate(shipment.pickupDate).format(),
    shipment.selectedQuote,
  )
  const deliveryDate = isDelivered
    ? shipment.tracking?.deliveryDateActual
    : estimatedDelivery?.toDate()
  const carrierDetails = getCarrierDetails(shipment)
  const price = shipment.selectedQuote?.amount
    ? `$${formatNumber(shipment.selectedQuote.amount)}`
    : null

  const trackingEvent = shipment.tracking?.currentLocation

  return (
    <>
      {trackingEvent && (
        <div className="flex items-center gap-x-2 bg-white/30 px-4 py-2 border border-fv-blue-200 mb-4 justify-between whitespace-nowrap overflow-hidden flex-none">
          <span className="text-sm italic pr-2 text-ellipsis overflow-hidden">
            {trackingEvent?.summary}
          </span>
          <span className="text-sm italic">
            {formatTrackingDateTime(trackingEvent.eventDateTime, {
              timeFormat: 'h:mm A',
              dateFormat: 'MM/DD/YYYY',
            })}
          </span>
        </div>
      )}

      <div className="bg-white flex-1 p-4 border border-fv-gray">
        <List className="w-full" fcp0 lcp0>
          <ListItem className="!items-start">
            <Icon
              icon="calendar-day"
              className="text-fv-orange"
              transform="down-2"
            />
            <p className="mb-0">
              Pick up on {fvDate(shipment.pickupDate).format('MMM D, YYYY')}
              {origin?.company && (
                <>
                  <br />
                  {origin.company}
                </>
              )}
              <br />
              {origin ? `${origin.city}, ${origin.state}` : 'Unknown'}
            </p>
          </ListItem>

          <ListItem className="!items-start">
            <Icon
              icon="calendar-day"
              className="text-fv-green"
              transform="down-2"
            />
            <p className="mb-0">
              {isDelivered ? 'Delivered' : 'ETA'}{' '}
              {fvDate(deliveryDate).format('MMM D, YYYY')}
              {dest?.company && (
                <>
                  <br />
                  {dest.company}
                </>
              )}
              <br />
              {dest ? `${dest.city}, ${dest.state}` : 'Unknown'}
            </p>
          </ListItem>

          {shipment.tracking?.trackingNumber && (
            <ListItem>
              <Icon icon="dot-circle" className="text-fv-gray/50" />
              <span>
                {getTrackingNumLabel(shipment.workflow, shipment.selectedQuote)}{' '}
                {shipment.tracking.trackingNumber}
              </span>
            </ListItem>
          )}

          <ListItem>
            <Icon icon="dot-circle" className="text-fv-gray/50" />
            <span>BOL# {shipment.bol?.bolNumber ?? shipment.loadId}</span>
          </ListItem>

          {shipment.glCode && (
            <ListItem>
              <Icon icon="dot-circle" className="text-fv-gray/50" />
              <span>GL Code: {shipment.glCode}</span>
            </ListItem>
          )}

          {(carrierDetails || price) && (
            <ListItem>
              <Icon icon="dot-circle" className="text-fv-gray/50" />
              <span>{[price, carrierDetails].filter(Boolean).join(' / ')}</span>
            </ListItem>
          )}

          <ListItem>
            <Icon icon="dot-circle" className="text-fv-gray/50" />
            <span>{displayEquipment(shipment.equipment.type)}</span>
          </ListItem>

          {shipment.bookedBy?.name && (
            <ListItem>
              <Icon icon="dot-circle" className="text-fv-gray/50" />
              <span>Booked by {shipment.bookedBy?.name}</span>
            </ListItem>
          )}

          <ListItem>
            <Icon icon="dot-circle" className="text-fv-gray/50" />
            <ShipmentItemTypeSummary load={shipment} />
          </ListItem>
        </List>
      </div>
    </>
  )
}
